/**
 * DiscountForm - Create/edit discount & promo code form
 *
 * Sections:
 * 1. Basic Info (name, description, isActive)
 * 2. Discount Value (type + value)
 * 3. Promo Code (optional code string)
 * 4. Scope (all_plans | specific_plans | account)
 *    - specific_plans: plan multi-select
 *    - account: tenant search + optional limit overrides
 * 5. Validity & Limits (dates, usage caps)
 */
import { useState, useEffect } from 'react';
import { cn } from '@respectly/ui';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Switch,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@respectly/ui';
import {
  Save,
  X,
  Loader2,
  Tag,
  Percent,
  DollarSign,
  Layers,
  Package,
  Users,
  ChevronDown,
  ChevronUp,
  Search,
} from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type {
  DiscountWithStats,
  CreateDiscountDto,
  PlanWithStats,
} from '@respectly/shared-types';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';

// ============================================
// Types & Schema
// ============================================

interface DiscountFormProps {
  discount?: DiscountWithStats;
  plans: PlanWithStats[];
  onSubmit: (data: CreateDiscountDto) => Promise<boolean>;
  onCancel: () => void;
  isLoading?: boolean;
}

const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200),
  description: z.string().max(2000).optional(),
  isActive: z.boolean(),
  type: z.enum(['percentage', 'fixed']),
  value: z.coerce.number().positive('Value must be positive'),
  code: z.string().max(50).optional().nullable(),
  scope: z.enum(['all_plans', 'specific_plans', 'account']),
  startsAt: z.string().optional().nullable(),
  expiresAt: z.string().optional().nullable(),
  maxUses: z.coerce
    .number()
    .int('Must be a whole number')
    .positive('Must be at least 1')
    .optional(),
});

type FormData = z.infer<typeof formSchema>;

// Limit fields available for account-scoped overrides (must exist in Plan schema)
const LIMIT_OVERRIDE_FIELDS: { key: string; label: string; group: string }[] = [
  { key: 'smsCreditsPerMonth', label: 'SMS Credits/Month', group: 'Messaging' },
  { key: 'emailCreditsPerMonth', label: 'Email Credits/Month', group: 'Messaging' },
  { key: 'aiCreditsPerMonth', label: 'AI Credits/Month', group: 'Messaging' },
  { key: 'maxUsers', label: 'Team Members (Users)', group: 'Team & Contacts' },
  { key: 'maxContacts', label: 'Max Contacts', group: 'Team & Contacts' },
  { key: 'maxForms', label: 'Max Forms', group: 'Team & Contacts' },
  { key: 'maxSmsPerBroadcast', label: 'Max SMS per Broadcast', group: 'Broadcasting' },
  { key: 'maxEmailPerBroadcast', label: 'Max Emails per Broadcast', group: 'Broadcasting' },
];

const LIMIT_GROUPS = ['Messaging', 'Team & Contacts', 'Broadcasting'];

// ============================================
// Component
// ============================================

export function DiscountForm({
  discount,
  plans,
  onSubmit,
  onCancel,
  isLoading,
}: DiscountFormProps) {
  const isEditing = !!discount;
  const [submitError, setSubmitError] = useState<string | null>(null);

  // For specific_plans scope: selected plan IDs
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>(
    discount?.planIds ?? []
  );

  // For account scope: tenant search
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantResults, setTenantResults] = useState<{ id: string; name: string }[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<{ id: string; name: string } | null>(
    discount?.tenantId
      ? { id: discount.tenantId, name: discount.tenantName ?? discount.tenantId }
      : null
  );

  // For account scope: limit overrides
  const [showLimitOverrides, setShowLimitOverrides] = useState(false);
  const [limitOverrides, setLimitOverrides] = useState<Record<string, string>>({});

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: discount?.name || '',
      description: discount?.description || '',
      isActive: discount?.isActive ?? true,
      type: discount?.type || 'percentage',
      value: discount?.value || 20,
      code: discount?.code || '',
      scope: discount?.scope || 'all_plans',
      startsAt: discount?.startsAt
        ? new Date(discount.startsAt).toISOString().split('T')[0]
        : '',
      expiresAt: discount?.expiresAt
        ? new Date(discount.expiresAt).toISOString().split('T')[0]
        : '',
      maxUses: discount?.maxUses ?? undefined,
    },
  });

  const watchScope = watch('scope');
  const watchType = watch('type');
  const watchValue = watch('value');

  // Search tenants
  useEffect(() => {
    if (!tenantSearch || tenantSearch.length < 2) {
      setTenantResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const api = getApiClient();
        const res = await api.get<{ data: { id: string; name: string }[] }>(
          `${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.LIST}?search=${encodeURIComponent(tenantSearch)}&limit=10`
        );
        setTenantResults(res.data ?? []);
      } catch {
        setTenantResults([]);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [tenantSearch]);

  const togglePlan = (id: string) => {
    setSelectedPlanIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const onFormSubmit = async (data: FormData) => {
    setSubmitError(null);
    try {
      const dto: CreateDiscountDto = {
        name: data.name,
        description: data.description || undefined,
        isActive: data.isActive,
        type: data.type,
        value: data.value,
        code: data.code ? data.code.toUpperCase() : null,
        scope: data.scope,
        planIds: data.scope === 'specific_plans' ? selectedPlanIds : undefined,
        tenantId: data.scope === 'account' ? (selectedTenant?.id ?? null) : null,
        startsAt: data.startsAt ? new Date(data.startsAt) : null,
        expiresAt: data.expiresAt ? new Date(data.expiresAt) : null,
        maxUses: data.maxUses ?? null,
      };

      const success = await onSubmit(dto);
      if (!success) {
        setSubmitError('Failed to save discount. Please try again.');
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Basic Info */}
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Tag className="h-4 w-4 text-slate-500" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Summer Launch Promo"
              {...register('name')}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Internal notes about this discount..."
              rows={2}
              {...register('description')}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <div>
              <Label htmlFor="isActive" className="font-medium">Active</Label>
              <p className="text-xs text-slate-500">Inactive discounts cannot be applied</p>
            </div>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Switch
                  id="isActive"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Discount Value */}
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Percent className="h-4 w-4 text-slate-500" />
            Discount Value
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Type *</Label>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">
                        <span className="flex items-center gap-2">
                          <Percent className="h-3.5 w-3.5" /> Percentage (%)
                        </span>
                      </SelectItem>
                      <SelectItem value="fixed">
                        <span className="flex items-center gap-2">
                          <DollarSign className="h-3.5 w-3.5" /> Fixed Amount ($)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="value">
                {watchType === 'percentage' ? 'Percentage (%)' : 'Amount (USD)'}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  {watchType === 'percentage' ? '%' : '$'}
                </span>
                <Input
                  id="value"
                  type="number"
                  min="0.01"
                  max={watchType === 'percentage' ? '100' : undefined}
                  step="any"
                  className={cn('pl-7', errors.value && 'border-red-500')}
                  {...register('value')}
                />
              </div>
              {errors.value && <p className="text-xs text-red-500">{errors.value.message}</p>}
              {watchType === 'percentage' && watchValue > 0 && watchValue <= 100 && (
                <p className="text-xs text-violet-600 dark:text-violet-400">
                  A $79/mo plan becomes ${(79 * (1 - watchValue / 100)).toFixed(2)}/mo
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Promo Code */}
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Tag className="h-4 w-4 text-slate-500" />
            Promo Code
            <span className="text-xs font-normal text-slate-400">(optional)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            id="code"
            placeholder="e.g., LAUNCH50"
            {...register('code')}
            onBlur={(e) => {
              setValue('code', e.target.value.toUpperCase());
            }}
            className="font-mono uppercase"
          />
          <p className="text-xs text-slate-400">
            Leave blank for a private discount applied automatically. Fill in to create a
            shareable promo code.
          </p>
        </CardContent>
      </Card>

      {/* Scope */}
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Layers className="h-4 w-4 text-slate-500" />
            Scope
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Controller
            name="scope"
            control={control}
            render={({ field }) => (
              <div className="grid gap-3 sm:grid-cols-3">
                {(
                  [
                    { value: 'all_plans', label: 'All Plans', icon: Layers, desc: 'Applies to all plan subscribers' },
                    { value: 'specific_plans', label: 'Specific Plans', icon: Package, desc: 'Select which plans are eligible' },
                    { value: 'account', label: 'Account-Specific', icon: Users, desc: 'Custom pricing for one account' },
                  ] as const
                ).map(({ value, label, icon: Icon, desc }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => field.onChange(value)}
                    className={cn(
                      'flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left transition-colors',
                      field.value === value
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Icon
                        className={cn(
                          'h-4 w-4',
                          field.value === value ? 'text-indigo-600' : 'text-slate-400'
                        )}
                      />
                      <span
                        className={cn(
                          'text-sm font-medium',
                          field.value === value
                            ? 'text-indigo-700 dark:text-indigo-400'
                            : 'text-slate-700 dark:text-slate-300'
                        )}
                      >
                        {label}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">{desc}</span>
                  </button>
                ))}
              </div>
            )}
          />

          {/* Specific Plans: plan multi-select */}
          {watchScope === 'specific_plans' && (
            <div className="space-y-2 pt-1">
              <Label>Select Plans</Label>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 max-h-48 overflow-y-auto">
                {plans.length === 0 ? (
                  <p className="text-sm text-slate-400 p-3">No plans available</p>
                ) : (
                  plans.map((plan) => (
                    <label
                      key={plan.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPlanIds.includes(plan.id)}
                        onChange={() => togglePlan(plan.id)}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        {plan.name}
                      </span>
                      <span className="text-xs text-slate-400 ml-auto">
                        ${plan.monthlyPrice}/mo
                      </span>
                    </label>
                  ))
                )}
              </div>
              {selectedPlanIds.length > 0 && (
                <p className="text-xs text-slate-500">
                  {selectedPlanIds.length} plan{selectedPlanIds.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          )}

          {/* Account scope: tenant search */}
          {watchScope === 'account' && (
            <div className="space-y-3 pt-1">
              <div className="space-y-2">
                <Label>Search Account</Label>
                {selectedTenant ? (
                  <div className="flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-700 px-3 py-2">
                    <span className="text-sm font-medium text-indigo-700 dark:text-indigo-400">
                      {selectedTenant.name}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedTenant(null)}
                      className="h-6 w-6 p-0 text-indigo-400 hover:text-red-500"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                    <Input
                      placeholder="Search by account name..."
                      value={tenantSearch}
                      onChange={(e) => setTenantSearch(e.target.value)}
                      className="pl-8"
                    />
                    {tenantResults.length > 0 && (
                      <div className="absolute z-10 top-full mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg divide-y divide-slate-100 dark:divide-slate-800 max-h-48 overflow-y-auto">
                        {tenantResults.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"
                            onClick={() => {
                              setSelectedTenant(t);
                              setTenantSearch('');
                              setTenantResults([]);
                            }}
                          >
                            {t.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Custom Limits Override (collapsible) */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowLimitOverrides((v) => !v)}
                  className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                >
                  {showLimitOverrides ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  Custom Limits (optional)
                </button>
                <p className="text-xs text-slate-400 mt-0.5 ml-6">
                  Override specific plan limits just for this account. Leave blank to keep plan defaults.
                </p>

                {showLimitOverrides && (
                  <div className="mt-3 space-y-4">
                    {LIMIT_GROUPS.map((group) => {
                      const groupFields = LIMIT_OVERRIDE_FIELDS.filter(
                        (f) => f.group === group
                      );
                      return (
                        <div key={group}>
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                            {group}
                          </p>
                          <div className="grid gap-2 sm:grid-cols-2">
                            {groupFields.map((field) => (
                              <div key={field.key} className="space-y-1">
                                <Label className="text-xs">{field.label}</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  placeholder="Plan default"
                                  value={limitOverrides[field.key] ?? ''}
                                  onChange={(e) =>
                                    setLimitOverrides((prev) => ({
                                      ...prev,
                                      [field.key]: e.target.value,
                                    }))
                                  }
                                  className="h-8 text-sm"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Validity & Limits */}
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Layers className="h-4 w-4 text-slate-500" />
            Validity &amp; Usage Limits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="startsAt">Start Date</Label>
              <Input id="startsAt" type="date" {...register('startsAt')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiresAt">Expiry Date</Label>
              <Input id="expiresAt" type="date" {...register('expiresAt')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxUses">Max Total Uses</Label>
              <Input
                id="maxUses"
                type="number"
                min="1"
                placeholder="Unlimited"
                {...register('maxUses', {
                  setValueAs: (v) =>
                    v === '' || v === null || v === undefined ? undefined : Number(v),
                })}
              />
              {errors.maxUses && (
                <p className="text-xs text-red-500">{errors.maxUses.message}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {submitError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
          <p className="text-sm text-red-700 dark:text-red-400">{submitError}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button type="button" variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-1.5" />
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading || (!isDirty && isEditing)}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-1.5" />
              {isEditing ? 'Save Changes' : 'Create Discount'}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
